<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import * as XLSX from 'xlsx';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';

// Format date helper function
function formatDate(dateString: any) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const hoursStr = String(hours).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
    }
}

const allData = ref<any[]>([]);
const elementsPerPage = ref(25);
const currentPage = ref(1);
const toast = useToast();
const showEmployeeModal = ref(false);
const selectedMaterialItem = ref<any>(null);
const showActionModal = ref(false);
const selectedRequestItem = ref<any>(null);
const selectedEmployees = ref<string[]>([]);
const employeeStatusMap = ref<Record<string, { status: 'done' | 'not-done' | 'pending', reason: string }>>({});
const showPreviewModal = ref(false);
const previewData = ref<any>(null);
const mobilizersList = ref<any[]>([]);
const selectedMobilizer = ref<string>('');

const getAuthToken = () => localStorage.getItem('token');
const baseUrl = process.env.VUE_APP_BASE_URL;

onMounted(async () => {
    try {
        const token = getAuthToken();
        
        // Fetch material requests
        const response = await fetch(`${baseUrl}api/operation/frontOfficeMaterialRequest`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        console.log('API Response:', data);

        if (data && data.success && data.data) {
            allData.value = data.data;
            console.log('Data loaded successfully:', allData.value);
            if (allData.value.length === 0) {
                toast.warning('No records found');
            }
        } else {
            console.log('Invalid response structure:', data);
            toast.error('Invalid data format received');
        }

        // Fetch mobilizers list
        const mobilizersResponse = await fetch(`${baseUrl}api/all/mobilizers`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const mobilizersData = await mobilizersResponse.json();
        console.log('Mobilizers Response:', mobilizersData);

        if (mobilizersData && mobilizersData.success && mobilizersData.data) {
            mobilizersList.value = mobilizersData.data;
            console.log('Mobilizers loaded successfully:', mobilizersList.value);
        }
    } catch (error) {
        console.error('API Error:', error);
        toast.error('Error fetching data');
    }
});

// Filters
const filters = ref({
    siteName: '',
    reqDate: '',
    approvedBy: '',
    status: ''
});

const filteredData = computed(() => {
    const data = Array.isArray(allData.value) ? allData.value : [];
    
    const filtered = data.filter((item: any) => {
        const includes = (str: any, query: string) => String(str || '').toLowerCase().includes(query.toLowerCase());
        
        const matchesSite = !filters.value.siteName || 
            includes(item.siteName || item.siteId?.siteName, filters.value.siteName);
            
        const matchesReqDate = !filters.value.reqDate || 
            (item.reqDate && formatDate(item.reqDate).includes(filters.value.reqDate));
            
        const matchesApprovedBy = !filters.value.approvedBy || 
            includes(item.approvedBy?.email, filters.value.approvedBy);
            
        const matchesStatus = !filters.value.status || 
            includes(item.status, filters.value.status);
        
        return matchesSite && matchesReqDate && matchesApprovedBy && matchesStatus;
    });
    
    return filtered.slice(
        (currentPage.value - 1) * elementsPerPage.value,
        currentPage.value * elementsPerPage.value
    );
});

function num_pages() {
    const data = Array.isArray(allData.value) ? allData.value : [];
    return Math.ceil(data.length / elementsPerPage.value);
}

function change_page(page: number) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}

function clearFilters() {
    filters.value = {
        siteName: '',
        reqDate: '',
        approvedBy: '',
        status: ''
    };
}

function downloadExcel() {
    const formattedData = allData.value.map((item: any) => {
        const materials = item.materialItems?.map((mat: any) => {
            const employees = mat.employees?.map((emp: any) => 
                `${emp.employeeName} (${emp.size})`
            ).join(', ') || 'No employees';
            return `${mat.itemName} - Size: ${mat.size}, Qty: ${mat.quantity}, Employees: ${employees}`;
        }).join('; ') || '-';

        return {
            'Request Date': formatDate(item.reqDate),
            'Site Name': item.siteName || item.siteId?.siteName || '-',
            'Material Items & Employees': materials,
            'Approved By': item.approvedBy?.email || '-',
            'Approved Date': formatDate(item.approvedDate),
            'Status': item.status || '-'
        };
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Front Office Material Requests");
    XLSX.writeFile(workbook, 'front-office-material-requests.xlsx');
}

function openEmployeeModal(materialItems: any) {
    selectedMaterialItem.value = materialItems;
    showEmployeeModal.value = true;
}

function closeEmployeeModal() {
    showEmployeeModal.value = false;
    selectedMaterialItem.value = null;
}

function getTotalEmployeeCount(materialItems: any) {
    let count = 0;
    materialItems?.forEach((item: any) => {
        count += item.employees?.length || 0;
    });
    return count;
}

function openActionModal(item: any) {
    selectedRequestItem.value = item;
    selectedEmployees.value = [];
    employeeStatusMap.value = {};
    
    // Initialize status map for all employees
    item.materialItems?.forEach((mat: any) => {
        mat.employees?.forEach((emp: any) => {
            employeeStatusMap.value[emp.employeeId._id] = {
                status: 'pending',
                reason: ''
            };
        });
    });
    
    showActionModal.value = true;
}

function closeActionModal() {
    showActionModal.value = false;
    selectedRequestItem.value = null;
    selectedEmployees.value = [];
    employeeStatusMap.value = {};
}

function toggleEmployeeSelection(employeeId: string) {
    const index = selectedEmployees.value.indexOf(employeeId);
    if (index > -1) {
        selectedEmployees.value.splice(index, 1);
    } else {
        selectedEmployees.value.push(employeeId);
    }
}

function toggleAllEmployees() {
    if (!selectedRequestItem.value?.materialItems) return;
    
    const allEmployeeIds: string[] = [];
    selectedRequestItem.value.materialItems.forEach((item: any) => {
        item.employees?.forEach((emp: any) => {
            allEmployeeIds.push(emp.employeeId._id);
        });
    });
    
    if (selectedEmployees.value.length === allEmployeeIds.length) {
        selectedEmployees.value = [];
    } else {
        selectedEmployees.value = [...allEmployeeIds];
    }
}

function isEmployeeSelected(employeeId: string) {
    return selectedEmployees.value.includes(employeeId);
}

function isAllSelected() {
    if (!selectedRequestItem.value?.materialItems) return false;
    
    const allEmployeeIds: string[] = [];
    selectedRequestItem.value.materialItems.forEach((item: any) => {
        item.employees?.forEach((emp: any) => {
            allEmployeeIds.push(emp.employeeId._id);
        });
    });
    
    return allEmployeeIds.length > 0 && selectedEmployees.value.length === allEmployeeIds.length;
}

function setEmployeeStatus(employeeId: string, status: 'done' | 'not-done') {
    if (employeeStatusMap.value[employeeId]) {
        employeeStatusMap.value[employeeId].status = status;
        if (status === 'done') {
            employeeStatusMap.value[employeeId].reason = '';
        }
    }
}

function updateEmployeeReason(employeeId: string, event: Event) {
    const target = event.target as HTMLInputElement;
    if (employeeStatusMap.value[employeeId]) {
        employeeStatusMap.value[employeeId].reason = target.value;
    }
}

function getCompletedCount() {
    return Object.values(employeeStatusMap.value).filter(emp => emp.status === 'done').length;
}

function getNotDoneCount() {
    return Object.values(employeeStatusMap.value).filter(emp => emp.status === 'not-done').length;
}

function canProceed() {
    const allEmployees = Object.values(employeeStatusMap.value);
    const hasUnprocessed = allEmployees.some(emp => emp.status === 'pending');
    const notDoneWithoutReason = allEmployees.some(emp => emp.status === 'not-done' && !emp.reason.trim());
    
    return !hasUnprocessed && !notDoneWithoutReason;
}

function handleProceed() {
    if (!canProceed()) {
        toast.error('Please complete all employee status and provide reasons for "Not Done" items');
        return;
    }
    
    // Prepare detailed preview data
    const detailedEmployees: any[] = [];
    selectedRequestItem.value.materialItems?.forEach((mat: any) => {
        mat.employees?.forEach((emp: any) => {
            const status = employeeStatusMap.value[emp.employeeId._id];
            detailedEmployees.push({
                itemName: mat.itemName,
                itemSize: mat.size,
                department: mat.department,
                employeeId: emp.employeeId._id,
                employeeName: emp.employeeName,
                employeeSize: emp.size,
                status: status.status,
                reason: status.reason
            });
        });
    });
    
    previewData.value = {
        requestId: selectedRequestItem.value._id,
        siteName: selectedRequestItem.value.siteName,
        category: selectedRequestItem.value.category,
        reqDate: selectedRequestItem.value.reqDate,
        status: selectedRequestItem.value.status,
        employees: detailedEmployees,
        summary: {
            total: Object.keys(employeeStatusMap.value).length,
            done: getCompletedCount(),
            notDone: getNotDoneCount()
        }
    };
    
    // Close action modal and open preview modal
    closeActionModal();
    showPreviewModal.value = true;
}

function closePreviewModal() {
    showPreviewModal.value = false;
    previewData.value = null;
    selectedMobilizer.value = '';
}

function updateMobilizer(event: Event) {
    const target = event.target as HTMLSelectElement;
    selectedMobilizer.value = target.value;
}

function canCreateOrder() {
    // Check if a mobilizer is selected
    return selectedMobilizer.value !== '';
}

async function createOrder() {
    if (!canCreateOrder()) {
        toast.error('Please select a mobilizer for this order');
        return;
    }

    try {
        const token = getAuthToken();
        
        // Get the selected mobilizer details
        const selectedMobilizerData = mobilizersList.value.find(m => m._id === selectedMobilizer.value);

        // Prepare employees data in the required format
        const employeesData = previewData.value.employees.map((emp: any) => ({
            itemName: emp.itemName,
            itemSize: emp.itemSize,
            employeeId: emp.employeeId,
            employeeName: emp.employeeName,
            status: emp.status,
            reason: emp.reason || ''
        }));

        const orderData = {
            frontOfficeMaterialRequestId: previewData.value.requestId,
            mobilizerId: selectedMobilizer.value,
            mobilizerName: selectedMobilizerData?.employeeName || '',
            employees: employeesData,
            summary: previewData.value.summary
        };
        
        console.log('Creating Order:', JSON.stringify(orderData, null, 2));
        
        const response = await fetch(`${baseUrl}api/operation/order-delivery`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });
        
        const data = await response.json();
        
        if (data.success) {
            toast.success('Order created successfully!');
            closePreviewModal();
            // Refresh the data
            setTimeout(() => {
                location.reload();
            }, 1500);
        } else {
            toast.error(data.message || 'Failed to create order');
        }
        
    } catch (error) {
        console.error('Error creating order:', error);
        toast.error('Error creating order');
    }
}
</script>

<template>
    <Breadcrumbs main="Front Office" title="Material Request" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer" @submit.prevent>
                        <!-- Download button -->
                        <div class="mb-3 d-flex justify-content-end">
                            <button type="button" class="btn btn-success" @click="downloadExcel">
                                <i class="fa fa-download me-2"></i>Download Excel
                            </button>
                        </div>

                        <!-- Filter section -->
                        <div class="mb-3">
                            <div class="row g-3">
                                <!-- Site Name Filter -->
                                <div class="col-md-3">
                                    <label class="form-label">Site Name</label>
                                    <input type="text" class="form-control" v-model="filters.siteName" placeholder="Filter site name...">
                                </div>
                                
                                <!-- Request Date Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Request Date</label>
                                    <input type="date" class="form-control" v-model="filters.reqDate">
                                </div>

                                <!-- Approved By Filter -->
                                <div class="col-md-3">
                                    <label class="form-label">Approved By</label>
                                    <input type="text" class="form-control" v-model="filters.approvedBy" placeholder="Filter approved by...">
                                </div>

                                <!-- Status Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Status</label>
                                    <select class="form-select" v-model="filters.status">
                                        <option value="">All</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </div>
                                
                                <!-- Clear Filters Button -->
                                <div class="col-md-2 d-flex align-items-end">
                                    <button type="button" class="btn btn-secondary w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Table -->
                        <table class="display table-striped table-hover table-bordered table" id="basic-1">
                            <thead>
                                <tr>
                                    <th class="sorting_asc" scope="col">SL</th>
                                    <th class="sorting_asc" scope="col">Request Date</th>
                                    <th class="sorting_asc" scope="col">Site Name</th>
                                    <th class="sorting_asc" scope="col">Material Items & Employee Names (Sizes)</th>
                                    <th class="sorting_asc" scope="col">Approved By</th>
                                    <th class="sorting_asc" scope="col">Status</th>
                                    <th class="sorting_asc" scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody v-if="!filteredData.length">
                                <tr class="odd">
                                    <td valign="top" colspan="7" class="dataTables_empty">
                                        No matching records found
                                        <br>
                                        <small class="text-muted">Debug: Total records: {{ allData.length }}</small>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr v-for="(item, index) in filteredData" :key="item._id">
                                    <td>{{ (currentPage - 1) * elementsPerPage + index + 1 }}</td>
                                    <td>{{ formatDate(item.reqDate) }}</td>
                                    <td>{{ item.siteName || item.siteId?.siteName || '-' }}</td>
                                    <td>
                                        <div v-if="item.materialItems?.length">
                                            <!-- Show first material item -->
                                            <div v-for="(mat, idx) in item.materialItems.slice(0, 1)" :key="idx" class="mb-2">
                                                <div class="small">
                                                    <strong>{{ mat.itemName }}</strong> - Size: <strong>{{ mat.size }}</strong>
                                                    <br>
                                                    <small class="text-muted">Qty: {{ mat.quantity }}</small>
                                                    <br>
                                                    <div v-if="mat.employees?.length" class="mt-1">
                                                        <div v-for="(emp, empIdx) in mat.employees.slice(0, 2)" :key="empIdx">
                                                            <small>• {{ emp.employeeName }} ({{ emp.size }})</small>
                                                        </div>
                                                        <small v-if="mat.employees.length > 2" class="text-info">
                                                            + {{ mat.employees.length - 2 }} more employees
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Show "View All" button if more items exist -->
                                            <button 
                                                v-if="item.materialItems.length > 1 || getTotalEmployeeCount(item.materialItems) > 2" 
                                                class="btn btn-link btn-sm p-0 mt-1"
                                                @click="openEmployeeModal(item.materialItems)">
                                                View All ({{ item.materialItems.length }} items, {{ getTotalEmployeeCount(item.materialItems) }} employees)
                                            </button>
                                        </div>
                                        <span v-else class="text-muted">No material items</span>
                                    </td>
                                    <td>
                                        {{ item.approvedBy?.email || '-' }}
                                        <br>
                                        <small class="text-muted" v-if="item.approvedDate">
                                            {{ formatDate(item.approvedDate) }}
                                        </small>
                                    </td>
                                    <td>
                                        <span :class="{
                                            'badge rounded-pill bg-success': item.status === 'Approved',
                                            'badge rounded-pill bg-warning': item.status === 'Pending',
                                            'badge rounded-pill bg-danger': item.status === 'Rejected'
                                        }">{{ item.status || 'Unknown' }}</span>
                                    </td>
                                    <td>
                                        <button 
                                            class="btn btn-primary btn-sm"
                                            @click="openActionModal(item)">
                                            <i class="fa fa-eye me-1"></i>View
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Pagination -->
                        <ul class="pagination py-2 justify-content-end pagination-primary">
                            <li class="page-item" @click="previousPage">
                                <a class="page-link">Previous</a>
                            </li>
                            <li class="page-item" 
                                v-for="i in num_pages()" 
                                :key="i" 
                                :class="[i == currentPage ? 'active' : '']" 
                                @click="change_page(i)">
                                <a class="page-link">{{ i }}</a>
                            </li>
                            <li class="page-item" @click="nextPage">
                                <a class="page-link">Next</a>
                            </li>
                        </ul>
                    </form>
                </div>
            </div>
        </div>
    </div>

    <!-- Employee Details Modal -->
    <div v-if="showEmployeeModal" class="custom-modal-overlay" @click.self="closeEmployeeModal">
        <div class="custom-modal-container">
            <div class="custom-modal-box">
                <div class="custom-modal-header">
                    <h5 class="modal-title">Material Items & Employee Details</h5>
                    <button type="button" class="btn-close" @click="closeEmployeeModal"></button>
                </div>
                <div class="custom-modal-body">
                    <div class="table-wrapper">
                        <table class="table table-bordered table-striped" style="border: 1.5px solid #dee2e6; width: 100%; table-layout: auto;">
                        <thead>
                            <tr style="background-color: #f8f9fa;">
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">SL</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Item Name</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Size</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Department</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Quantity</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Employee Name</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Employee Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(mat, idx) in selectedMaterialItem" :key="idx">
                                <tr v-if="!mat.employees?.length">
                                    <td style="border: 1.5px solid #dee2e6;">{{ idx + 1 }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ mat.itemName }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ mat.size }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ mat.department }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ mat.quantity }}</td>
                                    <td colspan="2" class="text-muted" style="border: 1.5px solid #dee2e6;">No employees</td>
                                </tr>
                                <tr v-else v-for="(emp, empIdx) in mat.employees" :key="emp._id">
                                    <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ idx + 1 }}</td>
                                    <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ mat.itemName }}</td>
                                    <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ mat.size }}</td>
                                    <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ mat.department }}</td>
                                    <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ mat.quantity }}</td>
                                    <td style="border: 2px solid #6c757d;">{{ emp.employeeName }}</td>
                                    <td style="border: 2px solid #6c757d;">{{ emp.size }}</td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    </div>
                </div>
                <div class="custom-modal-footer">
                    <div class="me-auto">
                        <strong>Total Items: {{ selectedMaterialItem?.length || 0 }} | Total Employees: {{ getTotalEmployeeCount(selectedMaterialItem) }}</strong>
                    </div>
                    <button type="button" class="btn btn-secondary" @click="closeEmployeeModal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="showPreviewModal" class="custom-modal-overlay" @click.self="closePreviewModal">
        <div class="custom-modal-container">
            <div class="custom-modal-box">
                <div class="custom-modal-header">
                    <h5 class="modal-title">Order Preview</h5>
                    <button type="button" class="btn-close" @click="closePreviewModal"></button>
                </div>
                <div class="custom-modal-body">
                    <!-- Order Summary -->
                    <div class="mb-4 p-3 bg-light rounded">
                        <h6 class="mb-3 fw-bold">Order Summary</h6>
                        <div class="row">
                            <div class="col-md-6">
                                <p><strong>Site:</strong> {{ previewData?.siteName }}</p>
                                <p><strong>Category:</strong> {{ previewData?.category }}</p>
                                <p><strong>Request Date:</strong> {{ formatDate(previewData?.reqDate) }}</p>
                            </div>
                            <div class="col-md-6">
                                <p><strong>Total Employees:</strong> {{ previewData?.summary.total }}</p>
                                <p><strong>Completed:</strong> <span class="badge bg-success">{{ previewData?.summary.done }}</span></p>
                                <p><strong>Not Done:</strong> <span class="badge bg-danger">{{ previewData?.summary.notDone }}</span></p>
                            </div>
                        </div>
                    </div>

                    <!-- Mobilizer Selection -->
                    <div class="mb-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                        <h6 class="mb-3 fw-bold">
                            <i class="fa fa-user me-2"></i>Select Mobilizer for this Order
                        </h6>
                        <div class="row align-items-center">
                            <div class="col-md-6">
                                <select 
                                    class="form-select" 
                                    v-model="selectedMobilizer"
                                    @change="updateMobilizer($event)"
                                    required>
                                    <option value="">-- Select a Mobilizer --</option>
                                    <option 
                                        v-for="mobilizer in mobilizersList" 
                                        :key="mobilizer._id" 
                                        :value="mobilizer._id">
                                        {{ mobilizer.employeeName }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-md-6">
                                <span v-if="selectedMobilizer" class="text-success">
                                    <i class="fa fa-check-circle me-1"></i>
                                    Mobilizer selected: <strong>{{ mobilizersList.find(m => m._id === selectedMobilizer)?.employeeName }}</strong>
                                </span>
                                <span v-else class="text-danger">
                                    <i class="fa fa-exclamation-circle me-1"></i>
                                    Please select a mobilizer
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Employee Details Table -->
                    <div class="table-wrapper">
                        <h6 class="mb-3 fw-bold">Employee Details</h6>
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr style="background-color: #f8f9fa;">
                                    <th>SL</th>
                                    <th>Item Name</th>
                                    <th>Item Size</th>
                                    <th>Department</th>
                                    <th>Employee Name</th>
                                    <th>Employee Size</th>
                                    <th>Status</th>
                                    <th>Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(emp, index) in previewData?.employees" :key="index"
                                    :class="{
                                        'table-success': emp.status === 'done',
                                        'table-danger': emp.status === 'not-done'
                                    }">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ emp.itemName }}</td>
                                    <td>{{ emp.itemSize }}</td>
                                    <td>{{ emp.department }}</td>
                                    <td>{{ emp.employeeName }}</td>
                                    <td>{{ emp.employeeSize }}</td>
                                    <td>
                                        <span :class="{
                                            'badge bg-success': emp.status === 'done',
                                            'badge bg-danger': emp.status === 'not-done'
                                        }">
                                            {{ emp.status === 'done' ? 'Done' : 'Not Done' }}
                                        </span>
                                    </td>
                                    <td>{{ emp.reason || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Statistics -->
                    <div class="mt-4 p-3 bg-light rounded">
                        <h6 class="mb-3 fw-bold">Order Statistics</h6>
                        <div class="row text-center">
                            <div class="col-md-4">
                                <div class="p-3 bg-white rounded shadow-sm">
                                    <h3 class="text-primary mb-0">{{ previewData?.summary.total }}</h3>
                                    <small class="text-muted">Total Employees</small>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="p-3 bg-white rounded shadow-sm">
                                    <h3 class="text-success mb-0">{{ previewData?.summary.done }}</h3>
                                    <small class="text-muted">Completed</small>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="p-3 bg-white rounded shadow-sm">
                                    <h3 class="text-danger mb-0">{{ previewData?.summary.notDone }}</h3>
                                    <small class="text-muted">Not Done</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="custom-modal-footer">
                    <div class="me-auto">
                        <span v-if="!canCreateOrder()" class="text-danger small">
                            <i class="fa fa-exclamation-triangle me-1"></i>Please select a mobilizer for this order
                        </span>
                        <span v-else class="text-success small">
                            <i class="fa fa-check-circle me-1"></i>Mobilizer selected
                        </span>
                    </div>
                    <button type="button" class="btn btn-secondary me-2" @click="closePreviewModal">
                        <i class="fa fa-times me-1"></i>Cancel
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-success" 
                        @click="createOrder"
                        :disabled="!canCreateOrder()"
                        :title="!canCreateOrder() ? 'Please select a mobilizer for this order' : ''">
                        <i class="fa fa-shopping-cart me-1"></i>Create Order
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Action Modal - View and Select Employees -->
    <div v-if="showActionModal" class="custom-modal-overlay" @click.self="closeActionModal">
        <div class="custom-modal-container">
            <div class="custom-modal-box">
                <div class="custom-modal-header">
                    <h5 class="modal-title">Employee Selection</h5>
                    <button type="button" class="btn-close" @click="closeActionModal"></button>
                </div>
                <div class="custom-modal-body">
                    <!-- Request Details -->
                    <div class="mb-3 p-3 bg-light rounded">
                        <div class="row">
                            <div class="col-md-6">
                                <strong>Site:</strong> {{ selectedRequestItem?.siteName }}
                            </div>
                            <div class="col-md-6">
                                <strong>Request Date:</strong> {{ formatDate(selectedRequestItem?.reqDate) }}
                            </div>
                            <div class="col-md-6 mt-2">
                                <strong>Status:</strong> 
                                <span :class="{
                                    'badge rounded-pill bg-success': selectedRequestItem?.status === 'Approved',
                                    'badge rounded-pill bg-warning': selectedRequestItem?.status === 'Pending',
                                    'badge rounded-pill bg-danger': selectedRequestItem?.status === 'Rejected'
                                }">{{ selectedRequestItem?.status }}</span>
                            </div>
                            <div class="col-md-6 mt-2">
                                <strong>Category:</strong> {{ selectedRequestItem?.category }}
                            </div>
                        </div>
                    </div>

                    <!-- Summary Stats -->
                    <div class="mb-3 p-2 bg-light rounded d-flex gap-3">
                        <div>
                            <span class="badge bg-success">Done: {{ getCompletedCount() }}</span>
                        </div>
                        <div>
                            <span class="badge bg-danger">Not Done: {{ getNotDoneCount() }}</span>
                        </div>
                        <div>
                            <span class="badge bg-secondary">Pending: {{ getTotalEmployeeCount(selectedRequestItem?.materialItems) - getCompletedCount() - getNotDoneCount() }}</span>
                        </div>
                    </div>

                    <!-- Employee List -->
                    <div class="table-wrapper">
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr style="background-color: #f8f9fa;">
                                    <th>SL</th>
                                    <th>Item Name</th>
                                    <th>Size</th>
                                    <th>Department</th>
                                    <th>Employee Name</th>
                                    <th>Employee Size</th>
                                    <th style="width: 200px;">Status</th>
                                    <th style="width: 250px;">Reason (If Not Done)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(mat, idx) in selectedRequestItem?.materialItems" :key="idx">
                                    <tr v-if="!mat.employees?.length">
                                        <td colspan="8" class="text-muted text-center">No employees for {{ mat.itemName }}</td>
                                    </tr>
                                    <tr v-else v-for="(emp, empIdx) in mat.employees" :key="emp.employeeId._id" 
                                        :class="{
                                            'table-success': employeeStatusMap[emp.employeeId._id]?.status === 'done',
                                            'table-danger': employeeStatusMap[emp.employeeId._id]?.status === 'not-done'
                                        }">
                                        <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="vertical-align: middle; background-color: #f8f9fa; font-weight: 500;">{{ idx + 1 }}</td>
                                        <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="vertical-align: middle; background-color: #f8f9fa; font-weight: 500;">{{ mat.itemName }}</td>
                                        <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="vertical-align: middle; background-color: #f8f9fa; font-weight: 500;">{{ mat.size }}</td>
                                        <td v-if="empIdx === 0" :rowspan="mat.employees.length" style="vertical-align: middle; background-color: #f8f9fa; font-weight: 500;">{{ mat.department }}</td>
                                        <td>{{ emp.employeeName }}</td>
                                        <td>{{ emp.size }}</td>
                                        <td>
                                            <div class="btn-group btn-group-sm w-100" role="group">
                                                <input 
                                                    type="radio" 
                                                    class="btn-check" 
                                                    :name="`status-${emp.employeeId._id}`" 
                                                    :id="`done-${emp.employeeId._id}`"
                                                    :checked="employeeStatusMap[emp.employeeId._id]?.status === 'done'"
                                                    @change="setEmployeeStatus(emp.employeeId._id, 'done')">
                                                <label class="btn btn-outline-success" :for="`done-${emp.employeeId._id}`">
                                                    <i class="fa fa-check"></i> Done
                                                </label>

                                                <input 
                                                    type="radio" 
                                                    class="btn-check" 
                                                    :name="`status-${emp.employeeId._id}`" 
                                                    :id="`not-done-${emp.employeeId._id}`"
                                                    :checked="employeeStatusMap[emp.employeeId._id]?.status === 'not-done'"
                                                    @change="setEmployeeStatus(emp.employeeId._id, 'not-done')">
                                                <label class="btn btn-outline-danger" :for="`not-done-${emp.employeeId._id}`">
                                                    <i class="fa fa-times"></i> Not Done
                                                </label>
                                            </div>
                                        </td>
                                        <td>
                                            <input 
                                                v-if="employeeStatusMap[emp.employeeId._id]?.status === 'not-done'"
                                                type="text" 
                                                class="form-control form-control-sm" 
                                                placeholder="Enter reason..."
                                                :value="employeeStatusMap[emp.employeeId._id]?.reason"
                                                @input="updateEmployeeReason(emp.employeeId._id, $event)"
                                                required>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="custom-modal-footer">
                    <div class="me-auto">
                        <strong>Total: {{ getTotalEmployeeCount(selectedRequestItem?.materialItems) }} | Done: {{ getCompletedCount() }} | Not Done: {{ getNotDoneCount() }}</strong>
                    </div>
                    <button type="button" class="btn btn-secondary me-2" @click="closeActionModal">Cancel</button>
                    <button 
                        type="button" 
                        class="btn btn-success" 
                        @click="handleProceed"
                        :disabled="!canProceed()"
                        :title="!canProceed() ? 'Please complete all employee status and provide reasons for Not Done items' : ''">
                        <i class="fa fa-check me-1"></i>Proceed
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.btn-link {
    text-decoration: none;
}

.btn-link:hover {
    text-decoration: underline;
}

.small {
    font-size: 0.875rem;
}

/* Custom Modal Styles */
.custom-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
    color: #000;
}

.custom-modal-container {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 95%;
    max-height: 90vh;
}

.custom-modal-box {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-width: 1200px;
    width: fit-content;
    max-height: 85vh;
}

.custom-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
    background-color: #f8f9fa;
}

.custom-modal-header .modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
}

.custom-modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: calc(85vh - 140px);
    flex: 1;
    color: #000;
}

.custom-modal-body * {
    color: #000 !important;
}

.custom-modal-body .badge {
    color: #fff !important;
}

.custom-modal-body table {
    color: #000;
}

.custom-modal-body table td,
.custom-modal-body table th {
    color: #000 !important;
}

.btn-group-sm .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.table-success td {
    background-color: #d1e7dd !important;
}

.table-danger td {
    background-color: #f8d7da !important;
}

.table-wrapper {
    width: 100%;
    overflow-x: auto;
}

.custom-modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-top: 1px solid #dee2e6;
    background-color: #f8f9fa;
}

.custom-modal-footer .me-auto {
    margin-right: auto;
}
</style>
